import { useState, useEffect } from "react";
import { ActionIcon, Popover, TextInput, ScrollArea, Text, UnstyledButton, Divider } from "@mantine/core";
import { Bookmark, Search } from "tabler-icons-react";
import { FormFieldConfig } from "@captiva/shared-types";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

interface TemplateReferenceMenuProps {
  fields: FormFieldConfig[];
  onInsert: (placeholder: string) => void;
  targetType: "email" | "whatsapp";
}

export function TemplateReferenceMenu({ fields, onInsert, targetType }: TemplateReferenceMenuProps) {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");
  const [metadataKeys, setMetadataKeys] = useState<Record<string, string[]>>({});

  useEffect(() => {
    let active = true;

    async function loadMetadata() {
      const keysMap: Record<string, string[]> = {};

      for (const f of fields) {
        if ((f.type === "select" || f.type === "multiSelect") && f.optionsSource) {
          const source = f.optionsSource;
          if (source.type === "static" && Array.isArray(source.options)) {
            const keys = new Set<string>();
            source.options.forEach((opt) => {
              if (opt && typeof opt === "object" && 'metadata' in opt && (opt as any).metadata) {
                Object.keys((opt as any).metadata).forEach((k) => keys.add(k));
              }
            });
            if (keys.size > 0) {
              keysMap[f.key] = Array.from(keys);
            }
          } else if (source.type === "firestore") {
            const docId = source.id || source.optionsSetId;
            if (docId) {
              try {
                const colName = source.collection || "flexFormSelectOptions";
                const docRef = doc(db, colName, docId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists() && active) {
                  const data = docSnap.data();
                  const options = data?.options;
                  if (Array.isArray(options)) {
                    const keys = new Set<string>();
                    options.forEach((opt) => {
                      if (opt && typeof opt === "object" && 'metadata' in opt && (opt as any).metadata) {
                        Object.keys((opt as any).metadata).forEach((k) => keys.add(k));
                      }
                    });
                    if (keys.size > 0) {
                      keysMap[f.key] = Array.from(keys);
                    }
                  }
                }
              } catch (err) {
                console.error(`Failed to load option set ${docId}:`, err);
              }
            }
          }
        }
      }

      if (active) {
        setMetadataKeys(keysMap);
      }
    }

    loadMetadata();

    return () => {
      active = false;
    };
  }, [fields]);

  const items: Array<{ placeholder: string; label: string; section: "Submitter Info" | "Form Fields" }> = [];

  if (targetType === "email") {
    items.push({
      placeholder: "{{submitterEmail}}",
      label: "Submitter Email",
      section: "Submitter Info"
    });
  }

  fields.forEach((f) => {
    const keyLabel = f.label || f.key;
    const displayKey = f.key.includes(" ") ? `[${f.key}]` : f.key;
    if (f.type === "select" || f.type === "multiSelect") {
      const keys = metadataKeys[f.key] || [];

      keys.forEach((metaKey) => {
        items.push({
          placeholder: `{{${displayKey}.${metaKey}}}`,
          label: `${keyLabel} - ${metaKey} Metadata`,
          section: "Form Fields"
        });
      });

      // Also allow referencing the selected value itself (e.g. {{customer}})
      items.push({
        placeholder: `{{${displayKey}}}`,
        label: `${keyLabel}`,
        section: "Form Fields"
      });
    } else {
      items.push({
        placeholder: `{{${displayKey}}}`,
        label: `${keyLabel}`,
        section: "Form Fields"
      });
    }
  });

  const filteredItems = items.filter(
    (item) =>
      item.label.toLowerCase().includes(search.toLowerCase()) ||
      item.placeholder.toLowerCase().includes(search.toLowerCase())
  );

  const submitterSectionItems = filteredItems.filter((i) => i.section === "Submitter Info");
  const formFieldsSectionItems = filteredItems.filter((i) => i.section === "Form Fields");

  return (
    <Popover
      opened={opened}
      onClose={() => {
        setOpened(false);
        setSearch("");
      }}
      target={
        <ActionIcon
          variant="outline"
          color="cap-blue"
          size="md"
          title="Insert Placeholder Reference"
          onClick={() => setOpened((o) => !o)}
        >
          <Bookmark size={16} />
        </ActionIcon>
      }
      width={320}
      position="bottom"
      placement="end"
      withArrow
      styles={{
        popover: {
          padding: 8,
        }
      }}
    >
      <TextInput
        placeholder="Search fields..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        icon={<Search size={14} />}
        size="xs"
        mb="xs"
        autoFocus
      />

      <ScrollArea style={{ height: 220 }} type="auto">
        {submitterSectionItems.length > 0 && (
          <>
            <Text size="xs" color="dimmed" weight={700} px="xs" py={4}>
              Submitter Info
            </Text>
            {submitterSectionItems.map((item) => (
              <UnstyledButton
                key={item.placeholder}
                onClick={() => {
                  onInsert(item.placeholder);
                  setOpened(false);
                  setSearch("");
                }}
                sx={(theme) => ({
                  display: "block",
                  width: "100%",
                  padding: "6px 10px",
                  borderRadius: theme.radius.sm,
                  "&:hover": {
                    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
                  },
                })}
              >
                <Text size="sm">{item.label}</Text>
                <Text size="xs" color="dimmed" style={{ fontFamily: "monospace" }}>
                  {item.placeholder}
                </Text>
              </UnstyledButton>
            ))}
            {formFieldsSectionItems.length > 0 && <Divider my={4} />}
          </>
        )}

        {formFieldsSectionItems.length > 0 && (
          <>
            <Text size="xs" color="dimmed" weight={700} px="xs" py={4}>
              Form Fields
            </Text>
            {formFieldsSectionItems.map((item) => (
              <UnstyledButton
                key={item.placeholder}
                onClick={() => {
                  onInsert(item.placeholder);
                  setOpened(false);
                  setSearch("");
                }}
                sx={(theme) => ({
                  display: "block",
                  width: "100%",
                  padding: "6px 10px",
                  borderRadius: theme.radius.sm,
                  "&:hover": {
                    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
                  },
                })}
              >
                <Text size="sm">{item.label}</Text>
                <Text size="xs" color="dimmed" style={{ fontFamily: "monospace" }}>
                  {item.placeholder}
                </Text>
              </UnstyledButton>
            ))}
          </>
        )}

        {filteredItems.length === 0 && (
          <Text size="sm" color="dimmed" align="center" mt="md">
            No matches found
          </Text>
        )}
      </ScrollArea>
    </Popover>
  );
}
